import type { MessageKey } from '../i18n/messages';
import {
  buildAssetOwnerPayoutProfilePatchBody,
  digitsOnly,
  extractAssetOwnerPayoutFromPayload,
  normalizeAccountHolderName,
  parseAssetOwnerPayoutTransferDay,
  validateAssetOwnerPayoutFields,
} from './assetOwnerPayoutBank';

/** True when the API returned a redacted account number (e.g. ****1511). */
export function isMaskedBankAccountNumber(value: string): boolean {
  return value.includes('*');
}

/** Prefer a full locally stored account number over an API-redacted value. */
export function mergePayoutFormPayload(
  stored: Record<string, unknown>,
  fromApi: Record<string, unknown>,
  countryId?: number | null,
): Record<string, unknown> {
  const merged: Record<string, unknown> = {
    ...stored,
    ...fromApi,
    ...(countryId != null ? { country_id: countryId } : {}),
  };

  const storedAccount =
    typeof stored.account_number === 'string' ? stored.account_number.trim() : '';
  const apiAccount =
    typeof fromApi.account_number === 'string' ? fromApi.account_number.trim() : '';

  if (
    isMaskedBankAccountNumber(apiAccount) &&
    storedAccount &&
    !isMaskedBankAccountNumber(storedAccount)
  ) {
    merged.account_number = storedAccount;
  }

  return merged;
}

function accountNumberChanged(
  current: string,
  baseline: string,
): boolean {
  if (isMaskedBankAccountNumber(current)) {
    return false;
  }
  if (!current.trim()) {
    return !isMaskedBankAccountNumber(baseline) && current !== baseline;
  }
  if (isMaskedBankAccountNumber(baseline)) {
    return current !== digitsOnly(baseline);
  }
  return current !== baseline;
}

/**
 * Fields that differ from the loaded baseline (for UI / local merge).
 * Masked account numbers are never treated as a change.
 */
export function collectAssetOwnerPayoutFieldChanges(
  currentPayload: Record<string, unknown>,
  baselinePayload: Record<string, unknown>,
): Record<string, unknown> {
  const current = extractAssetOwnerPayoutFromPayload(currentPayload);
  const baseline = extractAssetOwnerPayoutFromPayload(baselinePayload);
  const changes: Record<string, unknown> = {};

  const curHolder = normalizeAccountHolderName(current.account_holder_name);
  const baseHolder = normalizeAccountHolderName(baseline.account_holder_name);
  if (curHolder !== baseHolder) {
    changes.account_holder_name = curHolder;
  }

  if (current.payout_country !== baseline.payout_country) {
    changes.payout_country = current.payout_country;
  }
  if (current.bank_number !== baseline.bank_number) {
    changes.bank_number = current.bank_number;
  }
  if (current.branch_number !== baseline.branch_number) {
    changes.branch_number = current.branch_number;
  }
  if (current.routing_number !== baseline.routing_number) {
    changes.routing_number = current.routing_number;
  }
  if (current.account_type !== baseline.account_type) {
    changes.account_type = current.account_type;
  }
  if (current.payout_transfer_day !== baseline.payout_transfer_day) {
    changes.payout_transfer_day = current.payout_transfer_day;
  }

  if (accountNumberChanged(current.account_number, baseline.account_number)) {
    changes.account_number = current.account_number;
  }

  return changes;
}

export function isTransferDayOnlyPayoutChange(changes: Record<string, unknown>): boolean {
  return Object.keys(changes).length === 1 && 'payout_transfer_day' in changes;
}

/**
 * Resolve a full account number for PATCH: current full digits, else baseline/stored full digits.
 * Never returns a masked value.
 */
export function resolveAccountNumberForPayoutPatch(
  currentPayload: Record<string, unknown>,
  baselinePayload: Record<string, unknown>,
): string | null {
  const current = extractAssetOwnerPayoutFromPayload(currentPayload).account_number;
  const baseline = extractAssetOwnerPayoutFromPayload(baselinePayload).account_number;

  if (current && !isMaskedBankAccountNumber(current)) {
    if (isMaskedBankAccountNumber(baseline) && current === digitsOnly(baseline)) {
      // Accidental unmask of the visible tail only — not a full number.
      return baseline && !isMaskedBankAccountNumber(baseline) ? baseline : null;
    }
    return current;
  }

  if (baseline && !isMaskedBankAccountNumber(baseline)) {
    return baseline;
  }

  return null;
}

/**
 * Build a complete bank_account PATCH. The API rejects partial bank_account objects.
 */
export function buildAssetOwnerPayoutPatchBodyFromChanges(
  currentPayload: Record<string, unknown>,
  baselinePayload: Record<string, unknown>,
): {
  changes: Record<string, unknown>;
  patchBody: Record<string, unknown>;
  errorKey: MessageKey | null;
} {
  const changes = collectAssetOwnerPayoutFieldChanges(currentPayload, baselinePayload);
  if (Object.keys(changes).length === 0) {
    return { changes, patchBody: {}, errorKey: null };
  }

  if (isTransferDayOnlyPayoutChange(changes)) {
    const day = parseAssetOwnerPayoutTransferDay(changes.payout_transfer_day);
    if (day === '') {
      return {
        changes,
        patchBody: {},
        errorKey: 'register.assetOwnerPayout.error.transferDayRequired',
      };
    }
    return {
      changes,
      patchBody: { payout_transfer_day: day },
      errorKey: null,
    };
  }

  const resolvedAccountNumber = resolveAccountNumberForPayoutPatch(
    currentPayload,
    baselinePayload,
  );
  if (!resolvedAccountNumber) {
    return {
      changes,
      patchBody: {},
      errorKey: 'register.assetOwnerPayout.error.accountNumberReenter',
    };
  }

  const resolvedPayload: Record<string, unknown> = {
    ...baselinePayload,
    ...currentPayload,
    account_number: resolvedAccountNumber,
  };

  const errorKey = validateAssetOwnerPayoutFields(resolvedPayload);
  return {
    changes,
    patchBody: errorKey ? {} : buildAssetOwnerPayoutProfilePatchBody(resolvedPayload),
    errorKey,
  };
}
